import { consoleSink } from "./consoleSink";
import { isDev } from "./env";
import { sanitize } from "./redact";
import type { LogContext, LogEvent, LogLevel, Logger, LogSink } from "./types";

/**
 * Dispatcher + sink-seam (ADR-0011 D1/D2). Кожен `logger.<level>(msg, ctx)`:
 * будує `LogEvent` → **поріг рівня** за середовищем (§8) → `sanitize` (§7) →
 * **fan-out** у всі sink-и.
 *
 * - baseline `consoleSink` — хардвайр поза мутабельним масивом (незнімний, ADR-0011 D6);
 *   `addLogSink` лише ДОДАЄ транспорти (remote — post-MVP) — споживачі не змінюються;
 * - dispatcher **ніколи не кидає**: помилку sink-а глушимо тихим internal fallback
 *   (один голий `console.error` поза портом — НЕ через logger, щоб не рекурсити, §14.1).
 */
const SEVERITY: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

const extraSinks: LogSink[] = [];

export function addLogSink(sink: LogSink): void {
  extraSinks.push(sink);
}

/** Поріг читається на момент виклику: dev → `debug` (усе), prod → `warn` (≥ warn). */
function passesThreshold(level: LogLevel): boolean {
  const threshold: LogLevel = isDev() ? "debug" : "warn";
  return SEVERITY[level] >= SEVERITY[threshold];
}

function fallback(err: unknown): void {
  // Тихий internal fallback поза портом — НЕ logger (рекурсія, §14.1).
  console.error("[logger] sink failed", err);
}

function dispatch(level: LogLevel, message: string, ctx?: LogContext): void {
  if (!passesThreshold(level)) {
    return;
  }
  const event: LogEvent = {
    level,
    message,
    context: { ...(ctx ?? {}) },
    timestamp: new Date().toISOString(),
  };
  const safe = sanitize(event);

  for (const sink of [consoleSink, ...extraSinks]) {
    try {
      sink.write(safe);
    } catch (err) {
      fallback(err);
    }
  }
}

export const logger: Logger = {
  error: (message, ctx) => dispatch("error", message, ctx),
  warn: (message, ctx) => dispatch("warn", message, ctx),
  info: (message, ctx) => dispatch("info", message, ctx),
  debug: (message, ctx) => dispatch("debug", message, ctx),
};
